import staticPages from '@/seo/static-pages.json'

export const SITE_URL = 'https://druzhba-mebel.ru'
export const SITE_NAME = 'Мебельная фабрика «Дружба»'
export const DEFAULT_IMAGE = `${SITE_URL}/seo/default.jpg`

const ORGANIZATION = {
  '@type': ['FurnitureStore', 'Organization'],
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: 'Дружба-мебель',
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/seo/default.jpg`,
    width: 1200,
    height: 630
  },
  image: `${SITE_URL}/seo/home.jpg`,
  telephone: '+7 964 888-99-05',
  email: 'druzhba-mebel@mail.ru',
  priceRange: '₽₽',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Смоляной переулок, 2Б, лит. 1',
    addressLocality: 'Астрахань',
    postalCode: '414056',
    addressCountry: 'RU'
  },
  areaServed: {
    '@type': 'Country',
    name: 'Россия'
  }
}

function cleanText(value, maxLength = 300) {
  const result = String(value || '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()

  if (result.length <= maxLength) return result
  return `${result.slice(0, maxLength - 1).trim()}…`
}

export function absoluteUrl(value, fallback = DEFAULT_IMAGE) {
  const source = String(value || '').trim()
  if (!source) return fallback
  if (/^https?:\/\//i.test(source)) return source
  if (source.startsWith('//')) return `https:${source}`
  return `${SITE_URL}/${source.replace(/^\/+/, '')}`
}

function canonicalPath(path) {
  const source = String(path || '/').split('?')[0].split('#')[0]
  if (source === '/') return '/'
  return `/${source.replace(/^\/+|\/+$/g, '')}`
}

function breadcrumbs(path, title) {
  const normalized = canonicalPath(path)
  const items = [{
    '@type': 'ListItem',
    position: 1,
    name: 'Главная',
    item: `${SITE_URL}/`
  }]

  if (normalized !== '/') {
    if (normalized.startsWith('/catalog/')) {
      items.push({
        '@type': 'ListItem',
        position: 2,
        name: 'Каталог диванов',
        item: `${SITE_URL}/`
      })
    } else if (normalized.startsWith('/news/')) {
      items.push({
        '@type': 'ListItem',
        position: 2,
        name: 'Статьи',
        item: `${SITE_URL}/news`
      })
    }

    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: title,
      item: `${SITE_URL}${normalized}`
    })
  }

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items
  }
}

function graphForPage(page, pageEntity) {
  const canonical = `${SITE_URL}${canonicalPath(page.path)}`
  const graph = [
    ORGANIZATION,
    pageEntity || {
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: page.title,
      description: page.description,
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: SITE_NAME,
        url: SITE_URL
      },
      about: { '@id': `${SITE_URL}/#organization` },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: absoluteUrl(page.image),
        width: 1200,
        height: 630
      },
      inLanguage: 'ru-RU'
    },
    breadcrumbs(page.path, page.breadcrumbTitle || page.title)
  ]

  return {
    '@context': 'https://schema.org',
    '@graph': graph
  }
}

export function buildSeo(page = {}) {
  const title = String(page.title || SITE_NAME)
  const description = cleanText(page.description || 'Диваны и мягкая мебель напрямую от производителя.', 320)
  const path = canonicalPath(page.path || '/')
  const canonical = `${SITE_URL}${path}`
  const image = absoluteUrl(page.image)
  const robots = page.index === false ? 'noindex,nofollow,noarchive' : 'index,follow,max-image-preview:large'
  const type = page.type || 'website'
  const jsonLd = page.jsonLd || graphForPage({ ...page, title, description, path, image })

  const meta = [
    { vmid: 'description', name: 'description', content: description },
    { vmid: 'keywords', name: 'keywords', content: String(page.keywords || '') },
    { vmid: 'robots', name: 'robots', content: robots },
    { vmid: 'googlebot', name: 'googlebot', content: robots },
    { vmid: 'theme-color', name: 'theme-color', content: '#b07c50' },
    { vmid: 'og-locale', property: 'og:locale', content: 'ru_RU' },
    { vmid: 'og-type', property: 'og:type', content: type },
    { vmid: 'og-site-name', property: 'og:site_name', content: SITE_NAME },
    { vmid: 'og-url', property: 'og:url', content: canonical },
    { vmid: 'og-title', property: 'og:title', content: title },
    { vmid: 'og-description', property: 'og:description', content: description },
    { vmid: 'og-image', property: 'og:image', content: image },
    { vmid: 'og-image-secure', property: 'og:image:secure_url', content: image },
    { vmid: 'og-image-width', property: 'og:image:width', content: '1200' },
    { vmid: 'og-image-height', property: 'og:image:height', content: '630' },
    { vmid: 'og-image-alt', property: 'og:image:alt', content: page.imageAlt || title },
    { vmid: 'twitter-card', name: 'twitter:card', content: 'summary_large_image' },
    { vmid: 'twitter-title', name: 'twitter:title', content: title },
    { vmid: 'twitter-description', name: 'twitter:description', content: description },
    { vmid: 'twitter-image', name: 'twitter:image', content: image },
    { vmid: 'twitter-image-alt', name: 'twitter:image:alt', content: page.imageAlt || title }
  ]

  if (Array.isArray(page.extraMeta)) meta.push(...page.extraMeta)

  return {
    title,
    meta,
    link: [
      { vmid: 'canonical', rel: 'canonical', href: canonical }
    ],
    script: [{
      vmid: 'page-jsonld',
      type: 'application/ld+json',
      innerHTML: JSON.stringify(jsonLd)
    }],
    __dangerouslyDisableSanitizersByTagID: {
      'page-jsonld': ['innerHTML']
    }
  }
}

export function staticPageByPath(path) {
  const normalized = canonicalPath(path)
  return staticPages.find(page => canonicalPath(page.path) === normalized) || null
}

export function buildRouteSeo(route) {
  const path = canonicalPath(route && route.path)
  const staticPage = staticPageByPath(path)
  if (staticPage) return buildSeo(staticPage)

  if (/^\/catalog\/[^/]+$/.test(path)) {
    return buildSeo({
      path,
      title: 'Диван от производителя | Мебельная фабрика «Дружба»',
      description: 'Карточка мягкой мебели фабрики «Дружба»: фотографии, размеры, характеристики, варианты ткани, цена и оформление заказа.',
      keywords: 'купить диван, диван от производителя, диван цена, мебельная фабрика Дружба',
      image: '/seo/catalog.jpg',
      index: true
    })
  }

  if (/^\/news\/[^/]+$/.test(path)) {
    return buildSeo({
      path,
      title: 'Статья о выборе и уходе за диваном | Дружба',
      description: 'Практический материал мебельной фабрики «Дружба» о выборе, эксплуатации и уходе за мягкой мебелью.',
      image: '/seo/article.jpg',
      type: 'article',
      index: true
    })
  }

  if (/^\/reviews\/[^/]+$/.test(path)) {
    const productId = path.split('/').pop()
    return buildSeo({
      path: `/catalog/${productId}`,
      title: 'Отзывы о диване | Мебельная фабрика «Дружба»',
      description: 'Отзывы покупателей о выбранной модели дивана мебельной фабрики «Дружба».',
      image: `/seo/products/${productId}.jpg`,
      index: false
    })
  }

  return buildSeo({
    path,
    title: `Страница сайта | ${SITE_NAME}`,
    description: 'Страница мебельной фабрики «Дружба».',
    image: '/seo/default.jpg',
    index: false
  })
}

export function buildProductSeo(product, image) {
  if (!product) return buildRouteSeo({ path: '/catalog' })

  const id = String(product.id || '')
  const name = String(product.name || 'Диван')
  const price = Number(product.price || 0)
  const description = cleanText(product.full_description || product.description, 280) ||
    `${name} напрямую от мебельной фабрики «Дружба». Выбор ткани и комплектации, изготовление и доставка.`
  const path = `/catalog/${id}`
  const imageUrl = absoluteUrl(image, `${SITE_URL}/seo/products/${id}.jpg`)
  const offer = {
    '@type': 'Offer',
    url: `${SITE_URL}${path}`,
    priceCurrency: 'RUB',
    seller: { '@id': `${SITE_URL}/#organization` }
  }
  if (price > 0) offer.price = price.toFixed(2)

  const productEntity = {
    '@type': 'Product',
    '@id': `${SITE_URL}${path}#product`,
    name,
    sku: id,
    description,
    image: [imageUrl, `${SITE_URL}/seo/products/${id}.jpg`],
    brand: {
      '@type': 'Brand',
      name: 'Дружба'
    },
    manufacturer: { '@id': `${SITE_URL}/#organization` },
    offers: offer
  }

  const rating = Number(product.star || 0)
  const ratingCount = Number(product.review_count || 0)
  if (rating >= 1 && rating <= 5 && ratingCount > 0) {
    productEntity.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: rating,
      bestRating: 5,
      worstRating: 1,
      ratingCount
    }
  }

  return buildSeo({
    path,
    title: `${name} купить от производителя — цена ${price ? `${Math.round(price).toLocaleString('ru-RU')} ₽` : 'по запросу'} | Дружба`,
    description: `${description} ${price ? `Цена: ${Math.round(price).toLocaleString('ru-RU')} ₽.` : ''} Поможем подобрать ткань, проверить размеры и рассчитать доставку по России.`,
    keywords: `${name}, купить ${name.toLowerCase()}, ${name.toLowerCase()} цена, диван от производителя, диваны Астрахань`,
    image: imageUrl,
    imageAlt: `${name} — мебельная фабрика «Дружба»`,
    type: 'product',
    index: true,
    extraMeta: price > 0 ? [
      { vmid: 'product-price-amount', property: 'product:price:amount', content: price.toFixed(2) },
      { vmid: 'product-price-currency', property: 'product:price:currency', content: 'RUB' }
    ] : [],
    jsonLd: graphForPage({
      path,
      title: name,
      description,
      image: imageUrl,
      breadcrumbTitle: name
    }, productEntity)
  })
}

export function buildArticleSeo(article, image) {
  if (!article) return buildRouteSeo({ path: '/news' })

  const slug = String(article.slug || '')
  const path = `/news/${slug}`
  const title = String(article.meta_title || article.title || 'Статья о диванах')
  const description = cleanText(article.meta_description || article.excerpt, 320)
  const imageUrl = absoluteUrl(image, `${SITE_URL}/seo/news/${slug}.jpg`)

  const articleEntity = {
    '@type': 'Article',
    '@id': `${SITE_URL}${path}#article`,
    mainEntityOfPage: `${SITE_URL}${path}`,
    headline: String(article.title || title),
    description,
    image: [imageUrl, `${SITE_URL}/seo/news/${slug}.jpg`],
    datePublished: article.published_at || undefined,
    dateModified: article.updated_at || article.published_at || undefined,
    author: {
      '@type': 'Organization',
      name: article.author || 'Редакция фабрики «Дружба»',
      url: `${SITE_URL}/about`
    },
    publisher: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'ru-RU',
    articleSection: article.category || 'Советы покупателю'
  }

  return buildSeo({
    path,
    title,
    description,
    keywords: `${article.title || ''}, диваны, мягкая мебель, советы мебельной фабрики`,
    image: imageUrl,
    imageAlt: article.image_alt || article.title || title,
    type: 'article',
    index: true,
    extraMeta: [
      { vmid: 'article-published-time', property: 'article:published_time', content: String(article.published_at || '') },
      { vmid: 'article-modified-time', property: 'article:modified_time', content: String(article.updated_at || article.published_at || '') },
      { vmid: 'article-section', property: 'article:section', content: String(article.category || 'Советы покупателю') }
    ],
    jsonLd: graphForPage({
      path,
      title,
      description,
      image: imageUrl,
      breadcrumbTitle: article.title || title
    }, articleEntity)
  })
}
